import * as React from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import moment from 'moment'

import { NewsModel } from 'app/models/newsModel'
import { GroupModel } from 'app/models/groupModel'
import { TIME_FORMAT } from 'app/constants/date'
import {
  NEWS_KEY,
  editMessageRequest,
  fetchMessages,
} from 'app/containers/administration/newsQueries'
import {
  GROUPS_KEY,
  fetchGroups,
} from 'app/containers/administration/groupQueries'
import { EditMessageForm } from './components/EditMessageForm'
import { TableProvider, useTable } from '@/modules/drivers/context/TableContext'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { AdminIcon } from '@/modules/shared/components'
import {
  clearAdminSelection,
  setAdminSelectedCount,
  subscribeToAdminRemove,
} from '@/modules/shared/stores/adminSelectionStore'
import { Button } from '@/ui/button'
import { toast } from '@/lib/toasts/api'
import { AdminModal } from '../shared/AdminModal'
import { AdminColumn, AdminTable, AdminToolbar } from '../shared/AdminTable'

/** Nowy komunikat startuje z dzisiejszą datą i domyślnym priorytetem, tak jak dawny formularz. */
const emptyMessage = (): NewsModel =>
  ({
    text: '',
    status: '#459A18',
    deleted: false,
    groups: [],
    startDate: moment(),
    endDate: null,
  }) as unknown as NewsModel

/**
 * Zakładka KOMUNIKATY panelu admina - funkcyjna, na react-query.
 *
 * Zastępuje `app/components/administration/messages.tsx`. Dodawanie idzie teraz przez ten
 * sam modal co edycja (komunikat bez `id` trafia na `create`) - wcześniej był do tego
 * osobny, rozwijany formularz powielający te same pola.
 */
const AdminMessagesPageContent: React.FC = () => {
  const { t } = useLocalize()
  const queryClient = useQueryClient()
  const { selectedGroup, resetAll } = useTable()

  const [search, setSearch] = React.useState('')
  const [edited, setEdited] = React.useState<NewsModel | null>(null)

  const query = useQuery({ queryKey: NEWS_KEY, queryFn: fetchMessages })
  const groupsQuery = useQuery({
    queryKey: [...GROUPS_KEY, null],
    queryFn: () => fetchGroups(),
  })

  const groups: GroupModel[] = groupsQuery.data ?? []

  const messages = React.useMemo(() => {
    const all = query.data ?? []
    const needle = search.trim().toLowerCase()
    if (!needle) return all
    return all.filter((message) => message.text?.toLowerCase().includes(needle))
  }, [query.data, search])

  const selectedCount = selectedGroup?.ids.length ?? 0
  React.useEffect(() => {
    setAdminSelectedCount(selectedCount)
  }, [selectedCount])
  React.useEffect(() => () => clearAdminSelection(), [])

  const selectedRef = React.useRef<Array<string | number>>([])
  selectedRef.current = selectedGroup?.ids ?? []
  React.useEffect(
    () =>
      subscribeToAdminRemove(async () => {
        const ids = selectedRef.current.map(String)
        if (ids.length === 0) return
        // Kasowanie komunikatu to znacznik `deleted`, nie usunięcie rekordu - tak samo
        // jak dotąd (nie ma endpointu delete).
        await Promise.all(
          (query.data ?? [])
            .filter((message) => ids.includes(String(message.id)))
            .map((message) =>
              editMessageRequest({ ...message, deleted: true }),
            ),
        )
        resetAll()
        clearAdminSelection()
        await queryClient.invalidateQueries({ queryKey: NEWS_KEY })
        toast.success({
          title: t('administrationPanel-messages.message-deleted-title'),
        })
      }),
    [resetAll, queryClient, query.data, t],
  )

  /** Zielony = aktywny, pomarańczowy = wygasł, czerwony = usunięty. */
  const statusOf = (message: NewsModel) => {
    if (message.deleted) return { color: '#FF515A', icon: 'not-connected' }
    if (message.endDate != null && moment.utc(message.endDate) < moment())
      return { color: '#FFA23F', icon: 'toast_warning' }
    return { color: '#459A18', icon: 'connected' }
  }

  const date = (value?: any, fallback = '-') =>
    value ? moment.utc(value).local().format(TIME_FORMAT) : fallback

  const columns: Array<AdminColumn<NewsModel>> = [
    {
      key: 'status',
      header: '',
      width: 50,
      align: 'center',
      render: (message) => {
        const status = statusOf(message)
        return (
          <span
            className="tw-inline-flex tw-h-7 tw-w-7 tw-items-center tw-justify-center tw-rounded"
            style={{ background: status.color }}
          >
            <AdminIcon name={status.icon} className="white" />
          </span>
        )
      },
    },
    {
      key: 'created',
      header: t('administrationPanel-messages.created'),
      width: 130,
      render: (message) => date(message.created, '?'),
    },
    {
      key: 'startDate',
      header: t('administrationPanel-messages.startDate'),
      width: 130,
      render: (message) => date(message.startDate),
    },
    {
      key: 'endDate',
      header: t('administrationPanel-messages.endDate'),
      width: 130,
      render: (message) => date(message.endDate),
    },
    {
      key: 'groups',
      header: t('administrationPanel-messages.groups'),
      width: 300,
      render: (message) =>
        !message.groups || message.groups.length === 0
          ? t('administrationPanel-messages.all')
          : message.groups
              .map((id) => groups.find((group) => group.id === id)?.name ?? '?')
              .join(', '),
    },
    {
      key: 'priority',
      header: '',
      width: 24,
      render: (message) => (
        <span
          className="tw-inline-block tw-h-5 tw-w-6 tw-rounded"
          style={{ background: message.status }}
        />
      ),
    },
    { key: 'text', header: t('administrationPanel-messages.text') },
    {
      key: 'edit',
      header: '',
      width: 58,
      align: 'center',
      render: (message) => (
        <AdminIcon
          name="edit"
          title={t('operation.edit')}
          onClick={(event) => {
            event.stopPropagation()
            setEdited({
              ...message,
              startDate: moment.utc(message.startDate).local(),
              endDate:
                message.endDate != null
                  ? moment.utc(message.endDate).local()
                  : message.endDate,
            })
          }}
        />
      ),
    },
    {
      key: 'delete',
      header: '',
      width: 58,
      align: 'center',
      render: (message) =>
        message.deleted ? null : (
          <AdminIcon
            name="delete"
            title={t('operation.delete')}
            onClick={async (event) => {
              event.stopPropagation()
              await editMessageRequest({ ...message, deleted: true })
              await queryClient.invalidateQueries({ queryKey: NEWS_KEY })
              toast.warning({
                title: t('administrationPanel-messages.message-deleted-title'),
                text: t('administrationPanel-messages.message-deleted-details'),
              })
            }}
          />
        ),
    },
  ]

  return (
    <div className="tw-flex tw-min-h-0 tw-flex-1 tw-flex-col tw-bg-content-primary">
      <AdminToolbar search={search} onSearch={setSearch}>
        <Button
          variant="primary"
          size="sm"
          onClick={() => setEdited(emptyMessage())}
        >
          <AdminIcon name="add" className="white tw-h-4 tw-w-4" />
          {t('administrationPanel-messages.add-message')}
        </Button>
      </AdminToolbar>

      <AdminTable
        idPrefix="message"
        rows={messages}
        columns={columns}
        rowId={(message) => message.id}
        isLoading={query.isLoading}
      />

      {edited && (
        <AdminModal
          icon={null}
          hideFooter
          width={760}
          title={t('administrationPanel-messages.edit-message')}
          onClose={() => setEdited(null)}
        >
          <EditMessageForm
            editedMessage={edited}
            groups={groups}
            onClose={() => setEdited(null)}
          />
        </AdminModal>
      )}
    </div>
  )
}

export const AdminMessagesPage: React.FC = () => (
  <TableProvider>
    <AdminMessagesPageContent />
  </TableProvider>
)
